import { authApi, publicApi, siteApi } from "@api";
import { WSBlacklistUpdateEvent, WSBlacklistUpdateEventDetail } from "@constants";
import { Paths } from "@constants/paths";
import { useAppUpdate } from "@hooks/useAppUpdate.ts";
import { AppDispatch, RootState } from "@stores";
import auth from "@stores/auth";
import authSlice, { AuthSliceState } from "@stores/auth/slices.ts";
import preferenceSlice, { PreferenceSliceState } from "@stores/preference/slices.ts";
import { AuthStatus, BankAccount, UserMode } from "@types";
import logger from "@utils/logger";
import { FundingProfileType, GroupCriteriaType } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useWSBlacklistUpdate } from "./useAppWS";

interface UseAuthSessionOptions {
  onBlacklistUpdate?: (event: WSBlacklistUpdateEventDetail) => void;
}

/**
 * Get the current auth session.
 * @return { self, oauth, status, bankAccount, logout, bankInfos, userMode, latestDepositAmount, isBlacklisted }
 * - self: Current user profile
 * - oauth: Current auth token
 * - status: Current auth status
 * - bankAccount: Current user bank account
 * - logout: Logout function
 * - bankInfos: All bank information
 * - userMode: Current user mode, use for UI display
 * - latestDepositAmount: Latest deposit amount
 * - isBlacklisted: Whether the user is blacklisted
 */
const useAuthSession = (options?: UseAuthSessionOptions) => {
  const { self, oauth, bankInfos, ewalletInfos, latestDepositAmount, credential, fundingProfileSettings } = useSelector<
    RootState,
    AuthSliceState
  >((state) => state.auth);
  const { serverInfo } = useSelector<RootState, PreferenceSliceState>((state) => state.preferences);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { performAppReload } = useAppUpdate();

  const status = useMemo(() => {
    // Token not found, authenticating state
    if (oauth === undefined) {
      logger._console.log("[Auth] Token not found, authenticating...");
      return AuthStatus.Authenticating;
    }
    // Found token, but no profile, unauthenticated state
    // Can be used to redirect to login page
    if (!self || !oauth) {
      logger._console.log("[Auth] Token found, but no profile, Unauthenticated.", { self }, { oauth });
      return AuthStatus.Unauthenticated;
    }
    // Found token and profile, authenticated state
    logger._console.log("[Auth] Token and profile found, Authenticated.", { self }, { oauth });
    // Check if user needs to reset password
    if (self?.member?.pwExpireAt) {
      logger._console.log("[Auth] Found pwExpiredAt.", { self }, { oauth });
      const expiredAt = moment.utc(self?.member?.pwExpireAt).utcOffset(serverInfo?.utcOffset ?? 0);
      logger._console.log("[Auth] Found pwExpiredAt.", expiredAt.format(), moment().format());
      if (expiredAt.isBefore(moment())) {
        return AuthStatus.PasswordExpired;
      }
    }
    return AuthStatus.Authenticated;
  }, [oauth, self, serverInfo?.utcOffset]);

  const logout = useCallback(() => {
    // clear redux store
    dispatch(auth.slice.actions.logout());
    // reset api cache
    dispatch(siteApi.util.resetApiState());
    dispatch(authApi.util.resetApiState());
    dispatch(publicApi.util.resetApiState());
    // reset preference store
    dispatch(preferenceSlice.actions.reset());
    // redirect to home page (open mode)
    navigate(Paths.PRIVATE.HOME);
  }, [navigate, dispatch]);

  const fundingProfileList = useMemo(() => {
    const bankMap = bankInfos;
    const eWalletMap = ewalletInfos;

    const paymentMap = {
      ...Object.fromEntries(
        Object.entries(bankMap ?? {}).map(([key, value]) => [key, { ...value, type: FundingProfileType.Bank }]),
      ),
      ...Object.fromEntries(
        Object.entries(eWalletMap ?? {}).map(([key, value]) => [key, { ...value, type: FundingProfileType.EWallet }]),
      ),
    };
    return (
      self?.member?.fundingProfiles?.map((item) => {
        return {
          ...item,
          icon: paymentMap[item.key]?.icon,
          name: paymentMap[item.key]?.name,
          accountName: self?.member?.fullname ?? "",
        };
      }) || []
    );
  }, [self?.member?.fundingProfiles, bankInfos, ewalletInfos, self?.member?.fullname]);

  const bankAccount: BankAccount | null = useMemo(() => {
    const fundingProfile = fundingProfileList?.find((item) => item.isPrimary);
    if (fundingProfile) {
      return {
        bankAccNo: fundingProfile.accNo,
        bankAccName: fundingProfile.accountName,
        bankKey: fundingProfile.key,
        bankName: fundingProfile.name,
        bankIcon: fundingProfile.icon || null,
      };
    }
    return null;
  }, [fundingProfileList]);

  const isBlacklisted = useMemo(() => {
    return self?.member?.group?.group?.criteria?.type === GroupCriteriaType.Blacklist;
  }, [self]);

  const userMode = useMemo(() => {
    return status === AuthStatus.Authenticated ? UserMode.MEMBER : UserMode.GUEST;
  }, [status]);

  // Reload after login & has bank account
  useEffect(() => {
    if (status === AuthStatus.Authenticated && !!bankAccount) {
      // Reload app
      performAppReload();
    }
  }, [status, performAppReload, bankAccount]);

  // Sync blacklist status from WS
  useWSBlacklistUpdate({
    cb: async (event: WSBlacklistUpdateEvent) => {
      const result = await dispatch(
        authApi.endpoints.getProfile.initiate(undefined, {
          forceRefetch: true,
        }),
      );
      if (result.data) {
        dispatch(authSlice.actions.updateProfile(result.data));
      }
      // Call onBlacklistUpdate callback if provided
      options?.onBlacklistUpdate?.(event.detail);
    },
  });

  return {
    self,
    oauth,
    status,
    bankAccount,
    fundingProfileList,
    logout,
    bankInfos,
    ewalletInfos,
    userMode,
    latestDepositAmount,
    isBlacklisted,
    credential,
    fundingProfileSettings,
    legalName: self?.member?.fullname?.trim(),
    isAuthenticated: status === AuthStatus.Authenticated,
  };
};

export default useAuthSession;
